import React, { useEffect, useState } from 'react';
import { CocoEvent } from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { BroadcastEventType } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';

const renameTriggerParamsSchema = z.object({
  name: z.string().min(1),
  // The kebab menu that opens this action already knows which trigger it
  // targets, so it passes the id directly rather than relying on the shared
  // `ResourceSelectionModel` being targeted at ask/execute time.
  triggerId: z.string().optional(),
});

type Params = z.infer<typeof renameTriggerParamsSchema>;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type RenameTriggerActionClient = {
  resource: {
    workflow: Pick<TenantedClient['resource']['workflow'], 'renameTrigger'>;
  };
};

/**
 * Resource-level rename of a workflow trigger, mirroring
 * {@link import('../workflow/rename-workflow.action').RenameWorkflowAction}. The
 * trigger name lives on the shared trigger resource, so every workflow bound to
 * it reflects the new name.
 */
export class RenameTriggerAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Rename Trigger';
  description = 'Rename a trigger';
  inputSchema = renameTriggerParamsSchema;
  group = 'app';
  icon = 'clock';

  private _lastRenamedTriggerId: string | null = null;

  constructor(
    private readonly client: RenameTriggerActionClient,
    private readonly modalManager: ModalManager,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetTriggerId(context) !== null;
  }

  getPriority(context: ApplicableContext): number {
    return this.getTargetTriggerId(context) !== null ? 20 : 0;
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const triggerId = payload.triggerId ?? this.getTargetTriggerId(context);
    if (triggerId === null || triggerId === undefined) {
      return err([{ t: 'error', message: 'No trigger in scope' }]);
    }

    const name = payload.name.trim();
    const result = await this.client.resource.workflow.renameTrigger({
      triggerId,
      name,
    });

    if (result.isErr()) {
      const msg =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to rename trigger';
      toast.error(msg);
      return err([{ t: 'error', message: msg }]);
    }

    // Restream the resource lists (triggers page, folder contents, etc.) that
    // are backed by `ResourceUpdated` watchables so the renamed trigger shows
    // its new name without a manual refetch.
    emit(BroadcastEventType.ResourceUpdated, {
      orgId: this.orgId,
      resourceType: 'workflow_trigger',
      resourceId: triggerId,
    });

    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null) {
      context.markAsNotTargeted(selection);
    }

    this._lastRenamedTriggerId = triggerId;

    toast.success('Trigger renamed');
    return ok({});
  }

  track(): ActionTrackMetadata | null {
    if (this._lastRenamedTriggerId === null) {
      return null;
    }
    return {
      event: CocoEvent.TriggerRenamed,
      properties: { triggerId: this._lastRenamedTriggerId },
    };
  }

  ask(
    params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const triggerId = params.triggerId ?? this.getTargetTriggerId(context);
    const initialName =
      params.name ?? this.getInitialTriggerNameFromContext(context);
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };

        this.modalManager.push({
          view: () => (
            <RenameTriggerAskView
              triggerId={triggerId}
              initialName={initialName}
              onRespond={async (nextParams) => {
                await onRespond(nextParams);
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  private getTargetTriggerId(context: ApplicableContext): string | null {
    if (context.isInScope(ResourceSelectionModel)) {
      const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
      if (
        selection.length === 1 &&
        selection[0]?.selections.length === 1 &&
        selection[0]?.getResourceIdsForType(ResourceType.WorkflowTrigger)
          .length === 1
      ) {
        return (
          selection[0]?.getResourceIdsForType(
            ResourceType.WorkflowTrigger
          )[0] ?? null
        );
      }
    }
    return null;
  }

  private getInitialTriggerNameFromContext(
    context: ExecuteContext
  ): string | undefined {
    if (context.isInScope(ResourceSelectionModel)) {
      const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
      if (
        selection.length === 1 &&
        selection[0]?.selections.length === 1 &&
        selection[0]?.getResourceForType(ResourceType.WorkflowTrigger) !==
          undefined
      ) {
        return selection[0]?.getResourceForType(ResourceType.WorkflowTrigger)
          ?.name;
      }
    }
    return undefined;
  }
}

const RenameTriggerAskView: React.FC<{
  triggerId: string | null;
  initialName?: string;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ triggerId, initialName, onRespond, onCancel }) => {
  const [name, setName] = useState(initialName ?? '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialName !== undefined) {
      setName(initialName);
    }
  }, [initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0 || loading || triggerId === null) {
      return;
    }
    setLoading(true);
    // Thread the resolved trigger id back through so `execute` acts on the exact
    // trigger the modal was opened for, independent of selection state.
    await onRespond({ name: trimmed, triggerId });
    setLoading(false);
  };

  return (
    <ModalPageView
      title="Rename trigger"
      subtitle="Choose a new name for this trigger"
      className="px-4 py-4"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2">
          <label htmlFor="trigger-name" className="text-sm font-medium">
            Trigger name
          </label>
          <Input
            id="trigger-name"
            autoFocus
            placeholder="Trigger name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading || triggerId === null || name.trim().length === 0}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Rename'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
